import React, { useEffect, useState } from "react";
import { useAppSelector } from "../../Reducers/store";
import FormApi from "../../Api/FormApi";
import { FormResponseDto } from "../../Models/FormResponseDto";
import { FormDto } from "../../Models/FormDto";
import {
  ColumnDef,
  useReactTable,
  getCoreRowModel,
  getPaginationRowModel,
  flexRender,
} from "@tanstack/react-table";
import {
  Button,
  Select,
  TextInput,
  Datepicker,
  Table as FlowTable,
  Pagination,
} from "flowbite-react";
import { format } from "date-fns";
import { useNavigate } from "react-router-dom";
import { UserLevelNames } from "../../Models/UserLevel";
import { toast } from "react-toastify";

export default function AdminSubmissionList() {
  const [formResponses, setFormResponses] = useState<FormResponseDto[]>([]);
  const [forms, setForms] = useState<FormDto[]>([]);
  const [selectedFormId, setSelectedFormId] = useState<number | null>(null);
  const [searchText, setSearchText] = useState("");
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [pageIndex, setPageIndex] = useState(0);
  const pageSize = 20;
  const navigate = useNavigate();
  const { userModel } = useAppSelector((state) => state.user);

  useEffect(() => {
    if (userModel && userModel.userLevelId !== UserLevelNames.SuperAdmin) {
      toast.error(
        "You do not have permission to access this page. Please contact an administrator if you believe this is an error."
      );
      // Redirect to home or unauthorized page
      navigate("/"); // or another route like "/unauthorized"
    }
  }, [userModel, navigate]);

  const fetchSubmissions = async () => {
    const res = await FormApi.getAllFormResponses();
    setFormResponses(res.data);
  };

  const fetchForms = async () => {
    const res = await FormApi.getAllForms();
    setForms(res.data);
  };

  useEffect(() => {
    fetchForms();
    fetchSubmissions();
  }, [refreshKey]);

  const handleDownload = async (responseId: number) => {
    console.log("downloading report");
    try {
      const res = await FormApi.downloadResponseReport(responseId);
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `FormResponse_${responseId}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Error downloading report:", err);
      alert("Failed to download report.");
    }
  };

  const updateStatus = async (id: number, status: string) => {
    await FormApi.updateResponseStatus(id, status);
    setRefreshKey((prev) => prev + 1);
  };

  const filteredData = formResponses.filter((resp) => {
    const formMatch = !selectedFormId || resp.formId === selectedFormId;
    const searchMatch =
      !searchText ||
      resp.submittedByFirstName
        ?.toLowerCase()
        .includes(searchText.toLowerCase()) ||
      resp.submittedByLastName
        ?.toLowerCase()
        .includes(searchText.toLowerCase());
    const startMatch = !startDate || new Date(resp.submittedDate) >= startDate;
    const endMatch = !endDate || new Date(resp.submittedDate) <= endDate;
    return formMatch && searchMatch && startMatch && endMatch;
  });

  const pagedData = filteredData.slice(
    pageIndex * pageSize,
    (pageIndex + 1) * pageSize
  );

  const columns: ColumnDef<FormResponseDto>[] = [
    { header: "Form", accessorFn: (row) => row.formTitle },
    {
      header: "First Name",
      accessorFn: (row) => row.submittedByFirstName,
    },
    {
      header: "Last Name",
      accessorFn: (row) => row.submittedByLastName,
    },
    {
      header: "Date",
      accessorFn: (row) => format(new Date(row.submittedDate), "yyyy-MM-dd"),
    },
    {
      header: "Total Price",
      accessorFn: (row) => `$${(row.totalPrice ?? 0).toFixed(2)}`,
    },
    {
      header: "Status",
      accessorFn: (row) => row.status ?? "Pending",
    },
    {
      header: "Download",
      cell: ({ row }) => (
        <button
          onClick={() => handleDownload(row.original.responseId)}
          type="button"
          className="text-blue-600 hover:underline"
        >
          Download
        </button>
      ),
    },
    {
      header: "Actions",
      cell: ({ row }) => (
        <div className="flex gap-2">
          <Button
            size="xs"
            color="green"
            onClick={() => updateStatus(row.original.responseId, "Approved")}
          >
            Approve
          </Button>
          <Button
            size="xs"
            color="red"
            onClick={() => updateStatus(row.original.responseId, "Rejected")}
          >
            Reject
          </Button>
        </div>
      ),
    },
  ];

  const table = useReactTable({
    data: pagedData,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    manualPagination: true,
    pageCount: Math.ceil(filteredData.length / pageSize),
  });

  return (
    <div className="p-6 w-full">
      <h1 className="text-2xl font-bold mb-4">Form Submissions</h1>

      <div className="flex gap-4 mb-4">
        <Select
          onChange={(e) => setSelectedFormId(Number(e.target.value) || null)}
          value={selectedFormId ?? ""}
        >
          <option value="">All Forms</option>
          {forms.map((f) => (
            <option key={f.formId} value={f.formId}>
              {f.title}
            </option>
          ))}
        </Select>
        <TextInput
          placeholder="Search users"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
        />
        <Datepicker
          placeholder="Start Date"
          onSelectedDateChanged={setStartDate}
        />
        <Datepicker placeholder="End Date" onSelectedDateChanged={setEndDate} />
      </div>

      <FlowTable striped>
        <thead>
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <th key={header.id}>
                  {header.isPlaceholder
                    ? null
                    : (header.column.columnDef.header as string)}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody>
          {table.getRowModel().rows.map((row) => (
            <tr key={row.id}>
              {row.getVisibleCells().map((cell) => (
                <td key={cell.id}>
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </FlowTable>

      <div className="flex justify-center mt-4">
        <Pagination
          currentPage={pageIndex + 1}
          totalPages={Math.ceil(filteredData.length / pageSize)}
          onPageChange={(page) => setPageIndex(page - 1)}
        />
      </div>
    </div>
  );
}
